import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  initVisitTracking, 
  trackVisitStart, 
  trackPageExit, 
  updateConversionPath,
  trackPageLoadTime 
} from '../utils/analytics';

/**
 * Custom hook for automatic page visit tracking
 * Usage: useAnalytics() in your main App component or route components
 */
export const useAnalytics = () => {
  const location = useLocation();
  const cleanupRef = useRef(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Initialize visit tracking only once
    if (!isInitializedRef.current) {
      cleanupRef.current = initVisitTracking();
      isInitializedRef.current = true;
    }

    // Track page visit on route change
    const handlePageVisit = async () => {
      // Track page exit for previous page
      await trackPageExit();
      
      // Update conversion path
      updateConversionPath(location.pathname);
      
      // Track new page visit
      await trackVisitStart(location.pathname);
      
      // Track page load time
      setTimeout(() => {
        trackPageLoadTime();
      }, 100);
    };

    handlePageVisit();

    // Cleanup function
    return () => {
      trackPageExit();
    };
  }, [location.pathname]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);
};

/**
 * Hook for tracking specific conversion events
 * Usage: const { trackConversion } = useConversionTracking()
 */
export const useConversionTracking = () => {
  const trackConversion = (eventType, eventData = {}) => {
    // Import dynamically to avoid circular dependencies
    import('../utils/analytics').then(({ trackConversionEvent }) => {
      trackConversionEvent(eventType, eventData);
    });
  };

  return { trackConversion };
};

/**
 * Hook for tracking form interactions
 * Usage: const { trackFormStart, trackFormSubmit, trackFormError } = useFormTracking('booking')
 */
export const useFormTracking = (formName) => {
  const trackFormStart = () => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Form', 'Start', formName);
    });
  };

  const trackFormSubmit = (success = true) => {
    import('../utils/analytics').then(({ trackEvent, trackConversionEvent }) => {
      if (success) {
        trackEvent('Form', 'Submit Success', formName);
        if (formName === 'booking') {
          trackConversionEvent('booking_started', { form_name: formName });
        } else if (formName === 'contact') {
          trackConversionEvent('contact_form', { form_name: formName });
        }
      } else {
        trackEvent('Form', 'Submit Error', formName);
      }
    });
  };

  const trackFormError = (errorType) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Form', 'Error', `${formName} - ${errorType}`);
    });
  };

  const trackFieldInteraction = (fieldName) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Form', 'Field Interaction', `${formName} - ${fieldName}`);
    });
  };

  return { 
    trackFormStart, 
    trackFormSubmit, 
    trackFormError, 
    trackFieldInteraction 
  };
};

/**
 * Hook for tracking button clicks and interactions
 * Usage: const { trackButtonClick } = useInteractionTracking()
 */
export const useInteractionTracking = () => {
  const trackButtonClick = (buttonName, location = 'unknown') => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Interaction', 'Button Click', `${buttonName} - ${location}`);
    });
  };

  const trackLinkClick = (linkText, destination) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Interaction', 'Link Click', `${linkText} -> ${destination}`);
    });
  };

  const trackDownload = (fileName, fileType) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Interaction', 'Download', `${fileName} (${fileType})`);
    });
  };

  const trackSearch = (searchTerm, resultsCount = null) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Interaction', 'Search', searchTerm, resultsCount);
    });
  };

  return { 
    trackButtonClick, 
    trackLinkClick, 
    trackDownload, 
    trackSearch 
  };
};

/**
 * Hook for tracking errors and performance issues
 * Usage: const { trackError, trackPerformanceIssue } = useErrorTracking()
 */
export const useErrorTracking = () => {
  const trackError = (error, context = 'unknown') => {
    import('../utils/analytics').then(({ trackError: analyticsTrackError }) => {
      analyticsTrackError(error, context);
    });
  };

  const trackPerformanceIssue = (issueType, details) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('Performance', 'Issue', issueType, details);
    });
  };

  const trackAPIError = (endpoint, statusCode, errorMessage) => {
    import('../utils/analytics').then(({ trackEvent }) => {
      trackEvent('API', 'Error', `${endpoint} - ${statusCode}`, errorMessage);
    });
  };

  return { 
    trackError, 
    trackPerformanceIssue, 
    trackAPIError 
  };
};

export default useAnalytics;
